// ── Design decisions ──────────────────────────────────────────────────────────
// Cálculo puro de la Producción de un viaje: por paquete y totales.
//
// · Sin React, sin red. Recibe el viaje anidado y la fila customs_config
//   (getTaxConfig) y devuelve números redondeados a 2 decimales.
// · El impuesto de cada paquete sale de calcPackageTax (customsCalc.js), la
//   única fórmula de la app: R = FOB × (1 + flete + seguro), IVA siempre,
//   DAI sólo si R > umbral. Acá no vive ninguna tasa ni umbral.
// · Cada paquete se calcula sobre SU FOB; los totales se suman después.
// · Sin config completa no se calcula nada: sinConfig y ceros, la tabla avisa.
// · tipo_ingreso null se marca sinTipo para que la tabla lo señale en ámbar;
//   nunca se oculta.
// ─────────────────────────────────────────────────────────────────────────────

import { calcPackageTax, customsConfigComplete } from '../../utils/customsCalc';

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Lecturas bilingües: filas heredadas pueden traer quantity/unit_value.
export const itemFob = (it) =>
  (Number(it?.cantidad ?? it?.quantity) || 0) *
  (Number(it?.fob_unitario_usd ?? it?.unit_value) || 0);

export const paqueteFob = (p) => (p?.items || []).reduce((s, it) => s + itemFob(it), 0);

export function calcularPaquete(paquete, taxConfig, nombreCliente = '') {
  const tipo = paquete?.tipo_ingreso || null;
  const fob = r2(paqueteFob(paquete));
  const sinConfig = !customsConfigComplete(taxConfig);
  const t = sinConfig ? { r: 0, iva: 0, dai: 0, total: 0 } : calcPackageTax(fob, taxConfig);

  return {
    paqueteId: paquete?.id,
    cliente: nombreCliente || paquete?.nombre || '',
    customer_id: paquete?.customer_id || null,
    tipo_ingreso: tipo,
    sinTipo: !tipo,
    sinConfig,
    articulos: (paquete?.items || []).length,
    fob,
    r: t.r,
    umbral: sinConfig ? null : Number(taxConfig.threshold_usd),
    dai: t.dai,
    iva: t.iva,
    total: t.total,
  };
}

export function computeProduccion(viaje, taxConfig, customers = []) {
  const porId = new Map((customers || []).map((c) => [c.id, c]));
  const paquetes = [...(viaje?.paquetes || [])].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
  );

  const filas = paquetes.map((p) =>
    calcularPaquete(p, taxConfig, porId.get(p.customer_id)?.full_name || '')
  );

  const suma = (k) => r2(filas.reduce((s, f) => s + (f[k] || 0), 0));
  const totales = {
    fob: suma('fob'),
    r: suma('r'),
    dai: suma('dai'),
    iva: suma('iva'),
    total: suma('total'),
  };

  const resumen = {
    paquetes: filas.length,
    bultos: (viaje?.bultos || []).length,
    articulos: filas.reduce((s, f) => s + f.articulos, 0),
    fob: totales.fob,
    sinTipo: filas.filter((f) => f.sinTipo).length,
    sinConfig: !customsConfigComplete(taxConfig),
  };

  return { filas, totales, resumen };
}
